import React, { useMemo, useRef } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import 'components/NoteAddCN/NoteAddCN.css'
import { buildQuillModules, QUILL_FORMATS } from 'components/NoteAddCN/quillConfig'
import useMediaQuery from 'hooks/useMediaQuery'
import type { NotePlan, StagedImage } from './imageUploadWizard.types'

/**
 * One editor. Kept as its own component so each per-image editor owns its own
 * Quill ref and toolbar handlers rather than sharing one across the list.
 */
const NoteEditor: React.FC<{
	value: string
	onChange: (html: string) => void
	placeholder: string
}> = ({ value, onChange, placeholder }) => {
	const quillRef = useRef<ReactQuill>(null)
	const isMobile = useMediaQuery('only screen and (max-width: 47.9375rem)')
	const modules = useMemo(() => buildQuillModules({ isMobile, quillRef }), [isMobile])

	return (
		<ReactQuill
			ref={quillRef}
			theme="snow"
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			modules={modules}
			formats={QUILL_FORMATS}
		/>
	)
}

interface NoteComposeStepProps {
	plan: Exclude<NotePlan, 'none'>
	images: StagedImage[]
	sharedNoteHtml: string
	onSharedNoteChange: (html: string) => void
	onImageNoteChange: (id: string, html: string) => void
}

/**
 * Step 3: one editor for the whole batch, or one editor per image with that
 * image's thumbnail beside it so the user can see what they are describing.
 */
const NoteComposeStep: React.FC<NoteComposeStepProps> = ({
	plan,
	images,
	sharedNoteHtml,
	onSharedNoteChange,
	onImageNoteChange,
}) => {
	if (plan === 'single') {
		return (
			<div className="space-y-3">
				<p className="text-sm text-muted-foreground">
					This note will hold all {images.length} images.
				</p>
				<NoteEditor
					value={sharedNoteHtml}
					onChange={onSharedNoteChange}
					placeholder="Add a note..."
				/>
				<div className="flex flex-wrap gap-2">
					{images.map(image => (
						<img
							key={image.id}
							src={image.preview}
							alt={image.file.name}
							className="h-14 w-14 rounded-md border border-border object-cover"
						/>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				Each image gets its own note. Leave one blank to post it without text.
			</p>

			{images.map((image, index) => (
				<div
					key={image.id}
					className="rounded-lg border border-border p-3 sm:flex sm:items-start sm:gap-3"
				>
					<div className="mb-2 shrink-0 sm:mb-0">
						<img
							src={image.preview}
							alt={image.file.name}
							className="h-24 w-24 rounded-md border border-border object-cover"
						/>
						<div className="mt-1 w-24 truncate text-[11px] text-muted-foreground">
							{image.file.name}
						</div>
					</div>
					<div className="min-w-0 flex-1">
						<NoteEditor
							value={image.noteHtml}
							onChange={html => onImageNoteChange(image.id, html)}
							placeholder={'Note for image ' + (index + 1) + '...'}
						/>
					</div>
				</div>
			))}
		</div>
	)
}

export default NoteComposeStep
